import { Injectable } from '@angular/core';
import { ApiService } from '@quorum/api';
import { Observable } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { WorkOrderDocument } from '@quorum/models/xs-resource';

@Injectable({
  providedIn: 'root',
})
export class WorkOrderDocumentService {
  constructor(private apiService: ApiService) {}

  /**
   * Gets all documents for a work order
   * @param workOrderId The work order ID
   */
  getWorkOrderDocuments(workOrderId: string): Observable<WorkOrderDocument[]> {
    return this.apiService
      .get(`v/1/fixed-operations/work-orders/${workOrderId}/documents`)
      .pipe(map((response) => response));
  }

  /**
   * Deletes a work order document
   * @param workOrderId The work order ID
   * @param documentId The document ID
   */
  deleteWorkOrderDocument(workOrderId: string, documentId: string): Observable<void> {
    return this.apiService
      .delete(`v/1/fixed-operations/work-orders/${workOrderId}/documents/${documentId}`)
      .pipe(map((): void => void 0));
  }

  /**
   * Updates an existing work order document
   * @param workOrderId The work order ID
   * @param content The updated document content (HTML)
   * @param metadata Updated metadata for the document
   * @param updatedBy The user who updated the document
   */
  updateWorkOrderDocument(
    workOrderId: string,
    content: any,
    metadata: { inspectionTypeId: any; inspectionName: any },
    updatedBy: string
  ): Observable<WorkOrderDocument> {
    const now = new Date().toISOString();

    return this.getWorkOrderDocuments(workOrderId).pipe(
      map((documents) => {
        // CASE 1: If at least one document exists, update the first one
        if (documents && documents.length > 0) {
          const existingDoc = documents[0];
          const updatedDoc = new WorkOrderDocument({
            ...existingDoc,
            workOrderId,
            mpviResult: {
              content,
              createdTimestamp: existingDoc.mpviResult?.createdTimestamp || now,
              updatedTimestamp: now,
            },
            metadata: {
              inspectionName: metadata.inspectionName ?? existingDoc.metadata?.inspectionName,
              inspectionTypeId: metadata?.inspectionTypeId ?? existingDoc.metadata?.inspectionTypeId,
              completedBy: updatedBy,
              completedDate: now,
              documentId: existingDoc.SK,
            },
            updatedTimestamp: now,
          });

          return this.apiService
            .put<WorkOrderDocument>(
              `v/1/fixed-operations/work-orders/${workOrderId}/documents/${existingDoc.SK}`,
              updatedDoc
            )
            .pipe(
              map((response) => {
                return {
                  ...response,
                  SK: response.SK || existingDoc.SK,
                  metadata: {
                    ...response.metadata,
                    documentId: response.metadata?.documentId ?? existingDoc.SK,
                  },
                } as WorkOrderDocument;
              })
            );
        } else {
          const newDoc = new WorkOrderDocument({
            workOrderId,
            customerName: null,
            vehicleYear: null,
            vehicleMake: null,
            vehicleModel: null,
            vehicleVin: null,
            vehicleOdometer: null,
            workSummary: null,
            shopSupplies: null,
            taxes: null,
            additionalFees: null,
            totalAmount: null,
            asrItems: [],
            mpviResult: {
              content,
              createdTimestamp: now,
              updatedTimestamp: now,
            },
            mpviS3Url: null,
            futureServices: [],
            onstarContact: null,
            siriusxmContact: null,
            invoice: null,
            maintenanceMenu: null,
            additionalService: [],
            metadata: {
              inspectionName: metadata?.inspectionName,
              inspectionTypeId: metadata?.inspectionTypeId,
              completedBy: updatedBy,
              completedDate: now,
            },
            createdTimestamp: now,
            updatedTimestamp: now,
          });

          return this.apiService
            .post<WorkOrderDocument>(`v/1/fixed-operations/work-orders/${workOrderId}/documents`, newDoc)
            .pipe(
              map((response: WorkOrderDocument) => {
                const finalSK = response.SK ?? null;
                return {
                  ...response,
                  metadata: {
                    ...response.metadata,
                    documentId: response.metadata?.documentId ?? finalSK,
                  },
                } as WorkOrderDocument;
              })
            );
        }
      }),

      mergeMap((observableDoc) => observableDoc)
    );
  }
}
